/*
  Flickity based (used by other looping things in theme)
  Mirrors slider element controls + new inifnite loop/peek handling
*/
if (!customElements.get('looping-slider')) {
  customElements.define(
    'looping-slider',
    class LoopingSlider extends HTMLElement {
      constructor() {
        super();

        this.motionReduced = theme.config.motionReduced;
        this.desktopQuery = window.matchMedia('screen and (min-width: 768px)');
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        if (!this.motionReduced) this.hideForReveal();

        ['previous', 'next'].forEach((direction) => {
          this.addEventListener(`slider:${direction}`, () => {
            this.init();
            this.carousel?.[direction]();
          });
        });

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => this.select(this.items.indexOf(event.target) + 1));
        }

        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
        Motion.inView(this, this.reveal.bind(this));
      }

      get items() {
        return this._items = this._items || Array.from(this.children);
      }

      get cards() {
        return this.items.map((item) => item.firstElementChild).filter(Boolean);
      }

      get animationParams() {
        const isMobile = theme.config.mqlSmall || theme.config.isTouch;

        return {
          distance: isMobile ? 40 : 70,
          duration: isMobile ? 0.4 : 0.7,
          staggerDelay: isMobile ? 0.05 : 0.1,
        };
      }

      onBreakpointChange() {
        if (this.desktopQuery.matches) {
          this.init();
          return;
        }

        this.destroyCarousel();
      }

      init() {
        if (this.initialized || !this.desktopQuery.matches) return;
        if (this.items.length < 2) return;
        this.initialized = true;

        this.carousel = new Flickity(this, {
          accessibility: false,
          cellAlign: 'left',
          selectedAttraction: 0.014,
          friction: 0.33,
          pageDots: false,
          prevNextButtons: false,
          wrapAround: true,
          rightToLeft: theme.config.rtl,
        });

        this.carousel.on('change', this.onChange.bind(this));
        this.carousel.on('settle', this.onSettle.bind(this));
      }

      hideForReveal() {
        Motion.animate(this.items, { opacity: 0 }, { duration: 0 });
        Motion.animate(this.cards, { y: this.animationParams.distance }, { duration: 0 });
      }

      reveal() {
        if (this.motionReduced || this.revealed) return;
        this.revealed = true;

        const { distance, duration, staggerDelay } = this.animationParams;
        const options = { duration, delay: Motion.stagger(staggerDelay) };
        const cards = this.cards;

        Motion.animate(this.items, { opacity: [0, 1] }, options);
        Motion.animate(cards, { y: [distance, 0] }, options).finished.then(() => {
          cards.forEach((card) => card.style.removeProperty('transform'));
        });
      }

      select(index = 1) {
        this.init();
        this.carousel?.select(index - 1);
      }

      // The dim overlay lifts on arrival, not when the slide starts moving
      onSettle(index) {
        this.items.forEach((item, position) => item.classList.toggle('selected', position === index));
      }

      onChange(index) {
        this.dispatchEvent(
          new CustomEvent('slider:change', {
            detail: {
              currentPage: index + 1,
              currentElement: this.items[index],
            },
          })
        );
      }

      destroyCarousel() {
        this.carousel?.destroy();
        this.carousel = null;
        this.initialized = false;
        this.items.forEach((item) => item.classList.remove('selected'));
      }

      disconnectedCallback() {
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        this.destroyCarousel();
      }
    }
  );
}
